import React, { useState } from 'react';
import { Shield, Save, Loader2, AlertTriangle } from 'lucide-react';
import Card from '../../components/UI/Card';
import Button from '../../components/UI/Button';
import ToggleSwitch from '../../components/Settings/ToggleSwitch';

const SecuritySettings = () => {
  const [saving, setSaving] = useState(false);
  const [successMsg, setSuccessMsg] = useState(null);

  const [settings, setSettings] = useState({
    twofaRequired: false,
    sessionTimeout: 60,
    passwordMinLength: 8,
    passwordRequireUppercase: true,
    passwordRequireSpecial: false,
    ipAllowlistEnabled: false,
    ipAllowlist: '',
    auditLogRetention: 90,
  });

  const handleSave = async () => {
    setSaving(true);
    // TODO: Save security settings to backend
    await new Promise((r) => setTimeout(r, 800));
    setSaving(false);
    setSuccessMsg('Security settings saved.');
    setTimeout(() => setSuccessMsg(null), 3000);
  };

  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <h2 className="text-lg font-semibold text-white">Security</h2>
        <p className="text-sm text-gray-500 mt-1">
          Manage authentication, password policies, and access controls.
        </p>
      </div>

      {successMsg && (
        <div
          role="status"
          aria-live="polite"
          className="px-4 py-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-sm text-emerald-400"
        >
          {successMsg}
        </div>
      )}

      {/* Authentication */}
      <Card>
        <Card.Header>
          <div className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-orange-500/70" />
            <h3 className="text-base font-semibold text-gray-100">Authentication</h3>
          </div>
        </Card.Header>
        <Card.Body className="space-y-1">
          <ToggleSwitch
            label="Two-Factor Authentication"
            description="Require all users to enroll in 2FA before accessing the platform."
            enabled={settings.twofaRequired}
            onChange={(val) => setSettings((s) => ({ ...s, twofaRequired: val }))}
          />

          {/* Session Timeout slider */}
          <div className="pt-3 pb-1 border-b border-gray-800">
            <div className="flex items-center justify-between mb-1">
              <div>
                <p className="text-sm font-medium text-gray-200">Session Timeout</p>
                <p className="text-xs text-gray-500">Auto-logout after inactivity.</p>
              </div>
              <span className="text-sm font-semibold text-gray-200 min-w-[5rem] text-right">
                {settings.sessionTimeout} min
              </span>
            </div>
            <input
              type="range"
              min={5}
              max={480}
              step={5}
              value={settings.sessionTimeout}
              onChange={(e) =>
                setSettings((s) => ({ ...s, sessionTimeout: parseInt(e.target.value, 10) }))
              }
              aria-label="Session timeout in minutes"
              className="w-full accent-orange-500"
            />
            <div className="flex justify-between text-xs text-gray-500 mt-0.5">
              <span>5 min</span>
              <span>480 min</span>
            </div>
          </div>
        </Card.Body>
      </Card>

      {/* Password Policy */}
      <Card>
        <Card.Header>
          <h3 className="text-base font-semibold text-gray-100">Password Policy</h3>
        </Card.Header>
        <Card.Body className="space-y-4">
          <div>
            <label
              htmlFor="password_min_length"
              className="block text-sm font-medium text-gray-200 mb-1"
            >
              Minimum Password Length
            </label>
            <input
              id="password_min_length"
              type="number"
              min={1}
              max={128}
              value={settings.passwordMinLength}
              onChange={(e) =>
                setSettings((s) => ({
                  ...s,
                  passwordMinLength: parseInt(e.target.value, 10) || 1,
                }))
              }
              className="w-full rounded-lg border border-gray-700 bg-dark-900 px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
            />
          </div>

          <ToggleSwitch
            label="Require Uppercase"
            description="Passwords must contain at least one uppercase letter."
            enabled={settings.passwordRequireUppercase}
            onChange={(val) => setSettings((s) => ({ ...s, passwordRequireUppercase: val }))}
          />

          <ToggleSwitch
            label="Require Special Characters"
            description="Passwords must contain at least one special character (e.g. !@#$%)."
            enabled={settings.passwordRequireSpecial}
            onChange={(val) => setSettings((s) => ({ ...s, passwordRequireSpecial: val }))}
          />
        </Card.Body>
      </Card>

      {/* IP Allowlist */}
      <Card>
        <Card.Header>
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-500/70" />
            <h3 className="text-base font-semibold text-gray-100">IP Allowlist</h3>
          </div>
          <p className="text-sm text-gray-400 mt-0.5">
            Restrict access to specific IP addresses or CIDR ranges.
          </p>
        </Card.Header>
        <Card.Body className="space-y-4">
          <ToggleSwitch
            label="Enable IP Allowlist"
            description="Only allow access from approved IP addresses."
            enabled={settings.ipAllowlistEnabled}
            onChange={(val) => setSettings((s) => ({ ...s, ipAllowlistEnabled: val }))}
          />
          {settings.ipAllowlistEnabled && (
            <div>
              <label
                htmlFor="ip-allowlist"
                className="block text-sm font-medium text-gray-200 mb-1"
              >
                Allowed IPs (one per line)
              </label>
              <textarea
                id="ip-allowlist"
                rows={4}
                placeholder={"192.168.1.0/24\n10.0.0.1"}
                value={settings.ipAllowlist}
                onChange={(e) => setSettings((s) => ({ ...s, ipAllowlist: e.target.value }))}
                className="w-full rounded-lg border border-gray-700 bg-dark-900 px-3 py-2 text-sm text-white font-mono placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-orange-500"
              />
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Audit Log */}
      <Card>
        <Card.Header>
          <h3 className="text-base font-semibold text-gray-100">Audit Log</h3>
        </Card.Header>
        <Card.Body>
          <div>
            <label
              htmlFor="audit-retention"
              className="block text-sm font-medium text-gray-200 mb-1"
            >
              Retention Period (days)
            </label>
            <input
              id="audit-retention"
              type="number"
              min={7}
              max={365}
              value={settings.auditLogRetention}
              onChange={(e) =>
                setSettings((s) => ({
                  ...s,
                  auditLogRetention: parseInt(e.target.value, 10) || 90,
                }))
              }
              className="w-full rounded-lg border border-gray-700 bg-dark-900 px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
            />
            <p className="text-xs text-gray-500 mt-1">
              Audit logs older than this will be automatically deleted.
            </p>
          </div>
        </Card.Body>
      </Card>

      <div className="flex justify-end">
        <Button variant="primary" size="md" onClick={handleSave} disabled={saving}>
          {saving ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" />
              Save Security Settings
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default SecuritySettings;
